import React from "react";
import PropTypes from "prop-types";

import { isSameDay } from "date-fns";
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardMeta,
  Icon,
  Label,
} from "semantic-ui-react";
import { formatDate } from "utils";

import { useClientSync } from "data/hub/sync";

import { Popper } from "components/Popper";
import Tooltip from "components/Tooltip";
import UserAvatar from "components/UserAvatar";

function MeetingCard({ meeting, onEdit, onDelete }) {
  const { sync } = useClientSync(meeting.clientId);

  const dateTimeFormatter = formatDate("simpleDateTime");
  const timeFormatter = formatDate("simpleTime");
  const sameDayMeeting = isSameDay(meeting.startDate, meeting.endDate);

  return (
    <Card style={{ boxShadow: "none", margin: "0" }}>
      <CardContent style={{ padding: "1em" }}>
        <CardHeader
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "0.25em",
            fontSize: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.25em",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <Icon name="calendar alternate" size="small" />
            <span
              title={meeting.name}
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {meeting.name}
            </span>
          </div>

          {meeting.crmState ? (
            <Tooltip title="We are still saving your changes to this meeting in your CRM. This will be completed shortly.">
              <Label color="yellow" size="tiny">
                {meeting.crmState}
              </Label>
            </Tooltip>
          ) : (
            <ButtonGroup>
              <Button
                basic
                style={{ padding: "0.25em" }}
                title="Edit Meeting"
                aria-label="Edit Meeting"
                disabled={sync.meetings.inProgress}
                onClick={() => {
                  onEdit(meeting);
                }}
              >
                <Icon name="edit" />
              </Button>
              <Button
                basic
                style={{ padding: "0.25em", width: "1em" }}
                title="Delete Meeting"
                aria-label="Delete Meeting"
                disabled={sync.meetings.inProgress}
                onClick={() => {
                  onDelete(meeting);
                }}
              >
                <Icon name="trash" />
              </Button>
            </ButtonGroup>
          )}
        </CardHeader>
        <CardMeta>
          <span data-test="meeting-card-type">{meeting.type.name}</span>
        </CardMeta>

        <CardDescription
          style={{
            display: "flex",
            gap: "0.5em",
            flexDirection: "column",
            marginTop: "0.5em",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: ".2em",
              color: "black",
              ...(sameDayMeeting ? {} : { flexDirection: "column" }),
            }}
          >
            <span data-test="meeting-card-start-date">
              {dateTimeFormatter(meeting.startDate)}
            </span>
            <span>-</span>
            <span data-test="meeting-card-end-date">
              {sameDayMeeting
                ? timeFormatter(meeting.endDate)
                : dateTimeFormatter(meeting.endDate)}
            </span>
          </div>

          {meeting.attendees.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "0.5em",
                flexDirection: "column",
              }}
            >
              <span
                as="h5"
                style={{
                  fontSize: "14px",
                  color: "black",
                  margin: "0",
                }}
              >
                Attendees
              </span>

              <div style={{ display: "flex", gap: ".2em" }}>
                {meeting.attendees.slice(0, 3).map((attendee) => {
                  return <UserAvatar user={attendee} key={attendee.id} />;
                })}

                {meeting.attendees.length > 3 && (
                  <Popper
                    on="click"
                    trigger={
                      <span
                        className="underline_on_hover"
                        style={{ cursor: "pointer" }}
                      >
                        + {meeting.attendees.length - 3} more
                      </span>
                    }
                  >
                    <div
                      style={{
                        display: "grid",
                        gap: "0.5em",
                        gridTemplateColumns: `repeat(${
                          meeting.attendees.length - 3 > 2
                            ? 3
                            : meeting.attendees.length - 3
                        }, 1fr)`,
                      }}
                    >
                      {meeting.attendees.slice(3).map((attendee) => {
                        return <UserAvatar user={attendee} key={attendee.id} />;
                      })}
                    </div>
                  </Popper>
                )}
              </div>
            </div>
          )}
        </CardDescription>
      </CardContent>
    </Card>
  );
}

MeetingCard.propTypes = {
  meeting: PropTypes.shape({
    id: PropTypes.string,
    clientId: PropTypes.string,
    name: PropTypes.string,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    type: PropTypes.shape({ name: PropTypes.string }),
    crmState: PropTypes.string,
    attendees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

MeetingCard.defaultProps = { onEdit: () => {}, onDelete: () => {} };

export default MeetingCard;
